import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Shield,
  ShieldCheck,
  Gavel,
  Ban,
  ListChecks,
  Terminal,
  BarChart3,
  ScrollText,
  Award,
  Box,
  Package,
  Bug,
  Hammer,
  FolderTree,
  LayoutGrid,
  Rocket,
  Server,
  Sparkles,
  UploadCloud,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { useSpaceStore } from '@/stores/space.store';
import { usePendingRequestsStore } from '@/stores/pendingRequests.store';
import { type FeatureKey } from '@/config/games';
import { DeployPanel } from '@/components/config/DeployPanel';
import styles from './Sidebar.module.scss';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  feature?: FeatureKey;
  adminOnly?: boolean;   // admin total uniquement
  manageOnly?: boolean;  // admin OU gestion déléguée (manageAccess / canAssignGrades)
  page?: string; // clé de page du grade (ex. "sanctions", "config.blocks") — gating fin, scopé au projet actif
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

// Dashboard : page d'accueil, toujours visible pour un utilisateur connecté (l'accès est garanti par le guard).
const dashboardItem: NavItem = {
  path: '/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboard,
  feature: 'dashboard',
};

const navGroups: NavGroup[] = [
  {
    title: 'Information',
    items: [
      { path: '/logs', label: 'Logs', icon: ScrollText, feature: 'logs', page: 'logs' },
      { path: '/stats', label: 'Statistiques', icon: BarChart3, feature: 'stats', page: 'stats' },
    ],
  },
  {
    title: 'Modération',
    items: [
      { path: '/sanctions', label: 'Sanctions', icon: Gavel, feature: 'sanctions', page: 'sanctions' },
      { path: '/banwords', label: 'Mots interdits', icon: Ban, feature: 'banwords', page: 'banwords' },
      { path: '/whitelist', label: 'Whitelist', icon: ListChecks, feature: 'whitelist', page: 'whitelist' },
      { path: '/console', label: 'Server', icon: Terminal, feature: 'console', page: 'console' },
    ],
  },
  {
    title: 'Gestion',
    items: [
      { path: '/staff', label: 'Staff', icon: Shield, feature: 'staff', page: 'staff' },
      { path: '/players', label: 'Joueurs', icon: Users, feature: 'players', page: 'players' },
      { path: '/admin', label: 'Grades & accès', icon: ShieldCheck, manageOnly: true },
    ],
  },
];

// Item d'accueil de l'espace Configuration
const configHomeItem: NavItem = {
  path: '/config',
  label: 'Paramètres',
  icon: LayoutGrid,
};

// Navigation de l'espace Configuration (édition in-game des packs)
const configGroups: NavGroup[] = [
  {
    title: 'Contenu',
    items: [
      { path: '/config/blocks', label: 'Blocks', icon: Box, page: 'config.blocks' },
      { path: '/config/items', label: 'Items', icon: Package, page: 'config.items' },
      { path: '/config/entities', label: 'Entities', icon: Bug, page: 'config.entities' },
      { path: '/config/crafts', label: 'Crafts', icon: Hammer, page: 'config.crafts' },
    ],
  },
  {
    title: 'Grades',
    items: [
      { path: '/config/grades', label: 'Grades', icon: Award, page: 'config.grades' },
      { path: '/config/cosmetics', label: 'Création', icon: Sparkles, page: 'config.cosmetics' },
    ],
  },
  {
    title: 'Serveurs & Packs',
    items: [
      { path: '/config/files', label: 'Fichiers', icon: FolderTree, page: 'config.files' },
      { path: '/config/servers', label: 'Containers', icon: Server, page: 'config.containers' },
    ],
  },
];

// Item d'accueil de l'espace Déploiement
const deployHomeItem: NavItem = {
  path: '/deploiement',
  label: "Demande d'édit",
  icon: Rocket,
};

export function Sidebar({ open = false }: { open?: boolean }) {
  const { user, hasPage } = useAuthStore();
  const hasFeature = useGameStore((s) => s.hasFeature);
  const activeGameId = useGameStore((s) => s.activeGameId);
  const space = useSpaceStore((s) => s.space);
  const [deployOpen, setDeployOpen] = useState(false);
  const pendingCount = usePendingRequestsStore((s) => s.count);
  const processing = usePendingRequestsStore((s) => s.processing);
  const fetchPending = usePendingRequestsStore((s) => s.fetch);

  // Compteur de demandes en attente (badge sur « Demande d'édit ») : au montage + toutes les 30 s.
  useEffect(() => {
    fetchPending(activeGameId);
    const t = setInterval(() => fetchPending(activeGameId), 30000);
    return () => clearInterval(t);
  }, [activeGameId, fetchPending]);

  const canShow = (item: NavItem) =>
    (!item.adminOnly || !!user?.isAdmin) &&
    (!item.manageOnly || !!user?.isAdmin || !!user?.manageAccess) &&
    (!item.page || hasPage(item.page)) &&
    (!item.feature || hasFeature(item.feature));

  const renderLink = (item: NavItem) => {
    const Icon = item.icon;
    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
        >
          <span className={styles.navIcon}>
            <Icon size={20} strokeWidth={2} />
          </span>
          <span className={styles.navLabel}>{item.label}</span>
          {item.path === '/deploiement' && (
            processing ? (
              <Loader2 size={16} className={styles.navSpin} />
            ) : pendingCount > 0 ? (
              <span className={styles.navBadge}>{pendingCount}</span>
            ) : null
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <aside className={`${styles.sidebar} ${open ? styles.open : ''}`}>
      <div className={styles.logo}>
        <img src="/recube-logo.png" alt="Recube" className={styles.logoImg} />
      </div>

      <nav className={styles.nav}>
        {space === 'deploiement' ? (
          <ul className={styles.navList}>{renderLink(deployHomeItem)}</ul>
        ) : space === 'configuration' ? (
          <>
            <ul className={styles.navList}>{renderLink(configHomeItem)}</ul>
            {configGroups.map((group) => {
              const items = group.items.filter(canShow);
              if (items.length === 0) return null;
              return (
                <div key={group.title} className={styles.navGroup}>
                  <span className={styles.navGroupTitle}>{group.title}</span>
                  <ul className={styles.navList}>{items.map(renderLink)}</ul>
                </div>
              );
            })}
          </>
        ) : (
          <>
            {canShow(dashboardItem) && <ul className={styles.navList}>{renderLink(dashboardItem)}</ul>}
            {navGroups.map((group) => {
              const items = group.items.filter(canShow);
              if (items.length === 0) return null;
              return (
                <div key={group.title} className={styles.navGroup}>
                  <span className={styles.navGroupTitle}>{group.title}</span>
                  <ul className={styles.navList}>{items.map(renderLink)}</ul>
                </div>
              );
            })}
          </>
        )}
      </nav>

      {space === 'configuration' && (
        <>
          <button className={styles.deployBtn} onClick={() => setDeployOpen(true)}>
            <span className={styles.navIcon}><UploadCloud size={20} strokeWidth={2} /></span>
            <span className={styles.navLabel}>Envoyer</span>
          </button>
          <DeployPanel open={deployOpen} onClose={() => setDeployOpen(false)} />
        </>
      )}

      {user && (
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.gamertag || 'User'} />
            ) : (
              <span>{user.gamertag?.charAt(0).toUpperCase() || '?'}</span>
            )}
          </div>
          <div className={styles.userDetails}>
            <span className={styles.userName}>{user.gamertag || 'Utilisateur'}</span>
            <span className={styles.userRole}>{user.displayRole || user.role}</span>
          </div>
        </div>
      )}
    </aside>
  );
}
